/**
 * useOneSignalIdentity — keeps OneSignal's external_id in sync with the
 * signed-in Meetball user.
 *
 * external_id = user.id, matching the backend's existing OneSignal contact sync
 * (backend app/api/services/notifications/onesignal_contact_service.py, which
 * upserts users with identity.external_id = str(user.id)), so a notification
 * addressed to a Meetball user id reaches the right OneSignal contact.
 *
 * Behaviour:
 *   - Dormant when OneSignal is not configured (native / no VITE_ONESIGNAL_APP_ID):
 *     the effect returns before touching the SDK.
 *   - On a userId → OneSignal.login(userId) once readiness resolves.
 *   - On null after a prior login → OneSignal.logout().
 *   - lastLoginIdRef (what the SDK currently reflects) dedupes stable identities.
 *
 * Correctness fixes over the original PR #780 hook:
 *   (a) A whenOneSignalReady() rejection skips login/logout entirely and leaves
 *       lastLoginIdRef untouched, so the next effect run retries the same id.
 *       A login-path failure additionally emits onesignal_login_failed.
 *   (b) lastLoginIdRef is assigned only AFTER login()/logout() RESOLVES. A
 *       failed login therefore stays retriable, and a failed logout does not
 *       clear the ref (so we don't forget we're still logged in).
 *   (c) Reconcile-after-settle. A sign-out (or account switch) can land while an
 *       earlier login() is still in flight. We cannot un-send an already-issued
 *       SDK call, and gating logout on lastLoginIdRef alone raced: the ref still
 *       lagged the SDK (login not yet settled), so `null === null` skipped logout
 *       and the device stayed bound to the previous user's external_id (a
 *       cross-user push leak that survives reloads, since OneSignal persists
 *       identity). Instead we track the CURRENT desired id in desiredIdRef and,
 *       after each login/logout settles, re-check it: if the SDK no longer
 *       reflects the desired identity we run another pass, until stable (bounded
 *       by MAX_RECONCILE_PASSES as a defensive stop). A single in-flight chain is
 *       serialized via syncingRef, so overlapping effect runs never double-issue.
 *
 * This is external-system synchronisation (the OneSignal SDK), which is the one
 * sanctioned use of useEffect per the React patterns skill.
 */
import { useEffect, useRef } from 'react';
import OneSignal from 'react-onesignal';
import { isOneSignalConfigured, whenOneSignalReady } from '@lib/onesignal';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';

// Defensive upper bound on reconcile passes within a single drain. Each pass
// applies the LATEST desired value, so the loop terminates as soon as the
// identity stops changing; this cap only guards against a pathological
// never-settling flip and can never be reached in normal use.
const MAX_RECONCILE_PASSES = 5;

export function useOneSignalIdentity(userId: string | null): void {
  const { trackEvent } = useAmplitudeTracking();
  // The last identity the SDK actually reflects. Null means "logged out" (or
  // never logged in). Written only after login()/logout() resolves.
  const lastLoginIdRef = useRef<string | null>(null);
  // The identity we WANT the SDK to reflect right now, mirrored from the prop in
  // its own effect (never assigned during render, per Rules of React). The
  // reconcile loop always applies the latest value here.
  const desiredIdRef = useRef<string | null>(userId);
  // Serializes the async reconcile chain: while one drain is in flight, a new
  // effect run just updates desiredIdRef and lets the running loop pick it up.
  const syncingRef = useRef(false);
  // Set on unmount so an in-flight loop stops issuing further SDK calls / ref
  // writes for a hook instance that no longer exists.
  const unmountedRef = useRef(false);

  // Keep the desired identity in sync with the prop. Declared BEFORE the
  // reconcile effect so it runs first on each commit — the reconcile loop then
  // reads an up-to-date desiredIdRef.
  useEffect(() => {
    desiredIdRef.current = userId;
  }, [userId]);

  // Drive the SDK toward the desired identity, reconciling after each settle.
  useEffect(() => {
    // Dormant unless OneSignal is configured for this platform + environment.
    if (!isOneSignalConfigured()) return;
    // A drain is already running; it will observe the updated desiredIdRef.
    if (syncingRef.current) return;
    // Nothing to do if the SDK already reflects the desired identity.
    if (desiredIdRef.current === lastLoginIdRef.current) return;

    syncingRef.current = true;
    // Tracks whether the pass that ultimately fails was a LOGIN (a null id means a
    // logout pass or a pre-SDK readiness failure with no login intended). Used to
    // scope telemetry to login-path failures, independent of any later desired-id
    // change while the rejection unwinds.
    let failingLoginId: string | null = null;

    const drain = async (): Promise<void> => {
      let passes = 0;
      while (passes < MAX_RECONCILE_PASSES) {
        // Stop issuing SDK calls once this hook instance has unmounted.
        if (unmountedRef.current) return;

        const desired = desiredIdRef.current;
        // Reached a stable state — the SDK reflects what we want.
        if (desired === lastLoginIdRef.current) return;
        passes += 1;
        failingLoginId = desired;

        // Wait for OneSignal to be ready; rejects if init failed / never ran.
        await whenOneSignalReady();

        if (desired) {
          // Login path: associate the OneSignal contact with this Meetball user.
          await OneSignal.login(desired);
        } else {
          // Logout path: desired is null but the SDK still reflects a prior login.
          await OneSignal.logout();
        }
        // Settle: record what the SDK now reflects. The loop re-reads
        // desiredIdRef, so an identity that changed mid-flight triggers another
        // pass automatically.
        lastLoginIdRef.current = desired;
      }
    };

    void drain()
      .catch((error: unknown) => {
        // On any failure (readiness or login/logout) leave lastLoginIdRef
        // untouched so a later effect run retries. Telemetry is login-specific: a
        // failed LOGIN means the user won't receive notifications addressed to
        // their id (worth alerting on); a failed LOGOUT is best-effort (worst
        // case: a stale contact association), so it is logged but not tracked.
        logger.error('OneSignal: identity sync failed', sanitizeError(error));
        if (failingLoginId) {
          trackEvent(ANALYTICS_EVENTS.onesignal_login_failed, sanitizeError(error));
        }
      })
      .finally(() => {
        // Release the serialization guard so a subsequent desired-id change (or a
        // retriable failure above) can start a fresh drain.
        syncingRef.current = false;
      });
  }, [userId, trackEvent]);

  // Mark unmount so an in-flight drain halts. Kept in its own effect with empty
  // deps so it runs exactly once per mount lifecycle (StrictMode-safe: the mount
  // remounts reset unmountedRef via this effect's setup on the second mount).
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);
}
